/**
 * The companion surface is not a page tree — the only "route" is which
 * girlfriend is active, so the client uses a tiny hash router (no dependency,
 * deep-linkable, survives reload) instead of a full router. `#/c/<girlfriendId>`
 * selects a companion; `#/` (or empty) is the switcher's default. Pure parse /
 * format functions here; the Solid provider that binds them to `location.hash`
 * is `RouterProvider.tsx`.
 */

/** The parsed client route — just the active companion (null ⇒ none chosen yet). */
export interface Route {
  readonly companionId: string | null;
}

/** The default route when the hash is empty or unrecognized. */
export const HOME_ROUTE: Route = { companionId: null };

/** Parse a `location.hash` value (with or without the leading `#`) into a {@link Route}. */
export function parseHashRoute(hash: string): Route {
  const trimmed = hash.replace(/^#/, '').replace(/^\/+/, '');
  if (trimmed === '') {
    return HOME_ROUTE;
  }
  const segments = trimmed.split('/');
  if (segments[0] === 'c' && segments[1]) {
    return { companionId: safeDecode(segments[1]) };
  }
  return HOME_ROUTE;
}

/** Format a {@link Route} back into a `location.hash` string (always leading `#`). */
export function formatHashRoute(route: Route): string {
  if (route.companionId) {
    return `#/c/${encodeURIComponent(route.companionId)}`;
  }
  return '#/';
}

/** Decode a segment, falling back to the raw value if it is malformed (never throws). */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
